import clsx, { type ClassValue } from 'clsx';

/**
 * Merge class names with clsx for conditional and dynamic class composition.
 */
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

/**
 * Escapes a single CSV field per RFC 4180: wraps in double quotes when the value
 * contains a comma, quote, or newline, and doubles any embedded quotes.
 */
function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Builds a CSV string from a list of column definitions and row objects, then
 * triggers a browser download. CSV opens natively in Excel.
 *
 * A UTF-8 BOM is prepended so Excel renders accented characters correctly.
 *
 * @param filename Desired file name (a `.csv` extension is added if missing).
 * @param columns  Ordered columns: `header` is the label, `value` extracts the
 *                 cell value from a row.
 * @param rows     The data rows to export (already filtered/sorted by caller).
 */
export function exportRowsToCsv<T>(
  filename: string,
  columns: Array<{ header: string; value: (row: T) => unknown }>,
  rows: T[],
): void {
  const headerLine = columns.map((c) => escapeCsvField(c.header)).join(',');
  const dataLines = rows.map((row) => columns.map((c) => escapeCsvField(c.value(row))).join(','));
  const csv = [headerLine, ...dataLines].join('\r\n');

  // Prepend BOM for Excel UTF-8 detection.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Branding Color Utilities ────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6) return null;
  const num = parseInt(cleaned, 16);
  if (isNaN(num)) return null;
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * Math.max(0, Math.min(1, color)))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function applyColorPalette(prefix: string, hex: string): void {
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  const { h, s } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const shades: [string, number][] = [
    ['50', 96],
    ['100', 91],
    ['200', 82],
    ['300', 70],
    ['400', 58],
    ['500', 48],
    ['600', 40],
    ['700', 33],
    ['800', 27],
    ['900', 22],
    ['950', 14],
  ];
  const root = document.documentElement;
  for (const [shade, lightness] of shades) {
    root.style.setProperty(`--backoffice-${prefix}-${shade}`, hslToHex(h, s, lightness));
  }
}

/**
 * Apply firm branding colors as CSS custom properties on the document root.
 * - primaryColor  → --backoffice-primary-*  (tabs, active states)
 * - secondaryColor → --backoffice-secondary-* (buttons, CTAs)
 */
export function applyBrandingColors(
  primaryColor: string | null,
  secondaryColor: string | null,
): void {
  if (primaryColor) {
    applyColorPalette('primary', primaryColor);
  }
  if (secondaryColor) {
    applyColorPalette('secondary', secondaryColor);
  }
}
